export type PushSupport = {
  supported: boolean;
  reason?: string;
};

export type WebPushPayload = {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  tag?: string;
  url?: string;
  requireInteraction?: boolean;
  actions?: Array<{ action: string; title: string; icon?: string }>;
  data?: Record<string, unknown>;
};

const legacyServiceWorkerUrl = "/service-worker.js";
export const serviceWorkerUrl = "/sw.js";

export function getPushSupport(): PushSupport {
  if (typeof window === "undefined") return { supported: false, reason: "Notifications are available in a browser." };
  if (!window.isSecureContext) return { supported: false, reason: "Notifications require a secure HTTPS connection." };
  if (!("Notification" in window)) return { supported: false, reason: "This browser does not support notifications." };
  if (!("serviceWorker" in navigator)) return { supported: false, reason: "This browser does not support service workers." };
  if (!("PushManager" in window)) return { supported: false, reason: "This browser does not support push notifications." };
  if (!process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY)
    return { supported: false, reason: "Push notifications have not been configured for this workspace." };
  return { supported: true };
}

export async function getPushRegistration() {
  const support = getPushSupport();
  if (!support.supported) throw new Error(support.reason);

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations
      .filter((registration) => registration.active?.scriptURL.endsWith(legacyServiceWorkerUrl))
      .map((registration) => registration.unregister())
  );

  return navigator.serviceWorker.register(serviceWorkerUrl, { scope: "/" });
}

export async function requestNotificationPermission() {
  if (!("Notification" in window)) return "denied" as NotificationPermission;
  if (Notification.permission === "denied") return Notification.permission;
  return Notification.requestPermission();
}

export async function showClientTestNotification(payload: WebPushPayload) {
  const registration = await getPushRegistration();
  const worker = registration.active || registration.waiting || registration.installing;
  if (!worker) throw new Error("Notification service is still starting. Please try again.");
  worker.postMessage({ type: "SHOW_LOCAL_NOTIFICATION", payload });
}

export function urlB64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData, (character) => character.charCodeAt(0));
}
